import React, { FC, useRef, useState } from "react";
import { useField } from "formik";
import toast from "react-hot-toast";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axiosInstant from "../../api";
import { Button } from "../Button/Button";
import { PanelWrapper } from "./PanelWrapper";
import { briefAssetFileName } from "./constants";

export type BriefAssetKind = "logo" | "colors" | "font";

// Mirrors the API's per-kind allowlists (lib/briefBrandAssets ASSET_TYPES).
const ACCEPT: Record<BriefAssetKind, string> = {
    logo: ".png,.jpg,.jpeg,.svg,.pdf",
    colors: ".pdf,.png,.jpg,.jpeg,.txt,.ase",
    font: ".ttf,.otf,.woff,.woff2,.zip",
};

const IMAGE_URL = /\.(png|jpe?g|svg)$/i;

interface Props {
    name: string;
    kind: BriefAssetKind;
    body?: string;
    footer?: string;
    /** The upload endpoint for this kind; without one the field is read-only. */
    uploadUrl?: string;
    disabled?: boolean;
}

/**
 * A brief-asset file (TDC-1722). The upload only returns a URL — the field holds
 * it, and the host's normal save (brand PATCH or brief save) persists it.
 */
export const BriefAssetFile: FC<Props> = ({ name, kind, body, footer, uploadUrl, disabled }) => {
    const [field, , helpers] = useField<string>(name);
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const value = field.value || "";
    const buttonLabel = value ? "Replace" : "Upload";

    const upload = (file: File) => {
        if (!uploadUrl) return;
        const form = new FormData();
        form.append("file", file);
        setUploading(true);
        axiosInstant
            .post(uploadUrl, form)
            .then(({ data }) => {
                if (data?.url) helpers.setValue(data.url);
            })
            .catch((e: any) => {
                const messages: Record<string, string> = {
                    BriefAssetTypeNotAllowed: `${file.name}: only ${ACCEPT[kind].replace(/,/g, ", ")} files are allowed.`,
                    BriefAssetTooLarge: `${file.name} is over the 10 MB limit.`,
                };
                toast.error(
                    messages[e?.response?.data?.error] || `${file.name}: couldn't upload. Please try again.`,
                );
            })
            .finally(() => setUploading(false));
    };

    return (
        <PanelWrapper body={body} footer={footer}>
            <Box className="flex items-center gap-3 p-2 border border-surface-2 bg-surface-2 rounded-md min-h-[42px]">
                {value ? (
                    <>
                        {IMAGE_URL.test(value) && (
                            <img src={value} alt="" className="h-10 w-10 object-contain rounded bg-surface" />
                        )}
                        <a
                            href={value}
                            target="_blank"
                            rel="noreferrer"
                            className="text-accent text-[14px] truncate flex-1"
                        >
                            {briefAssetFileName(value)}
                        </a>
                        {uploadUrl && !disabled && (
                            <IconButton size="small" aria-label="Remove file" onClick={() => helpers.setValue("")}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        )}
                    </>
                ) : (
                    <Typography variant="body2" className="!text-text-2 flex-1">
                        No file
                    </Typography>
                )}
                {uploadUrl && !disabled && (
                    <Button
                        size="small"
                        variant="soft"
                        color="default"
                        disabled={uploading}
                        onClick={() => inputRef.current?.click()}
                    >
                        {uploading ? <CircularProgress size={16} /> : buttonLabel}
                    </Button>
                )}
                <input
                    ref={inputRef}
                    type="file"
                    hidden
                    accept={ACCEPT[kind]}
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = "";
                        if (file) upload(file);
                    }}
                />
            </Box>
        </PanelWrapper>
    );
};
